import { Formik } from 'formik';
import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { stepInfoFulfilled } from '../../../../actions/registerShipmentActions';
import { REGISTER_SHIPMENT_SET_INITIALVALUES } from '../../../../constants/actions/registerShipmentFormActions';
import { getPostcodeLabel } from '../../../../helpers/apiHelpers/registerShipmentHelpers';
import { getCurrentLangPretty } from '../../../../helpers/url/urlHelpers';
import useCountries from '../../../../hooks/useCountries';
import usePostCodes from '../../../../hooks/usePostCodes';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import IPageViewModel from '../../../../interfaces/umbracoNodes/pageViewModel';
import { IStore } from '../../../../reducers';
import { IRegisterShipmentReducer } from '../../../../reducers/registerShipmentReducer';
import Error from '../../../error';
import { RawLoader } from '../../../loading/loading';
import { WizardPageProps } from '../../../wizard/wizard';
import { requiresBusinessAccountForCompanySender } from './helpers';
import { IStepInfoFormInfo, StepInfoSchema } from './stepInfo.schema';
import * as StyledStepInfo from './stepInfo.styles';
import StepInfoForm from './stepInfoForm';

interface Props extends WizardPageProps {
	page: IPageViewModel;
}

const StepInfo: FC<Props> = ({ onSubmit, onPrevious, page }) => {
	const dispatch = useDispatch<any>();
	const registerShipmentReducer: IRegisterShipmentReducer = useSelector<IStore, IRegisterShipmentReducer>(
		store => store.registerShipmentReducer
	);
	const infoFormInfo: IStepInfoFormInfo = registerShipmentReducer.stepInfoFormInfo;
	const { initializedSender } = registerShipmentReducer;
	const { postCodes, postCodesLoading, postCodesError } = usePostCodes();
	const { countriesLoading, countriesError } = useCountries(getCurrentLangPretty(), true);

	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile, userLoading } = userProfileReducer;

	if (infoFormInfo.country.label === 'Ísland' && getCurrentLangPretty() === 'en') {
		infoFormInfo.country.label = 'Iceland';
	}

	if (userProfile && postCodes && !initializedSender) {
		infoFormInfo.senderSsn = userProfile.identificationNumber || '';
		infoFormInfo.senderName = userProfile.name || '';
		infoFormInfo.senderAddress = userProfile.currentAddress?.street1 || '';
		infoFormInfo.senderPostCode = {
			value: userProfile.currentAddress?.postalCode,
			label: getPostcodeLabel(userProfile.currentAddress?.postalCode, postCodes),
		};
		infoFormInfo.senderPhone =
			(userProfile.phoneNumber &&
			userProfile.phoneNumber.length === 11 &&
			userProfile.phoneNumber.substring(0, 4) === '+354'
				? userProfile.phoneNumber.substring(4)
				: userProfile.phoneNumber) || '';
		infoFormInfo.senderEmail = userProfile.email || '';
		dispatch({ type: REGISTER_SHIPMENT_SET_INITIALVALUES });
	}

	if (userLoading || postCodesLoading || countriesLoading) return <RawLoader />;
	if (countriesError || postCodesError)
		return <Error heading="error.error-heading" subText="error.error-countries" />;

	const shipmentFormInfo = registerShipmentReducer.stepShipmentFormInfo;

	const onStepInfoSubmit = values => {
		if (requiresBusinessAccountForCompanySender(values)) {
			return;
		}

		dispatch(stepInfoFulfilled(values));
		onSubmit(values);
	};

	return (
		<StyledStepInfo.Wrapper>
			<Formik
				initialValues={infoFormInfo}
				enableReinitialize
				validationSchema={StepInfoSchema}
				onSubmit={onStepInfoSubmit}>
				{({ values, setFieldTouched, setFieldValue, isValid, isSubmitting, errors, validateForm }) => (
					<StepInfoForm
						values={values}
						errors={errors}
						onPrevious={onPrevious}
						setFieldTouched={setFieldTouched}
						setFieldValue={setFieldValue}
						isSubmitting={isSubmitting}
						isValid={isValid}
						goingToPostbox={shipmentFormInfo.serviceOption === 'DPO'}
						page={page}
						validateForm={validateForm}
					/>
				)}
			</Formik>
		</StyledStepInfo.Wrapper>
	);
};

export default StepInfo;
